import { PREORDER_WEEKS } from "./NEW_DAYS";

/**
 * Supply availability, from ONIX code list 65 (`ProductAvailability`), as
 * delivered in `SupplyDetail/ProductAvailability`.
 *
 * Feeds send the individual two-digit code; the webshops act on the band it
 * falls in, so the enum names the band floors rather than every code. Read a
 * raw value with `parseAvailabilityCode` and turn a supply into a purchase
 * state with `getSupplyState`.
 *
 * Availability decides whether a supply can be sold at all. It does not say
 * whether the title is out yet: a forthcoming e-book carries code 20 from its
 * first delivery while its print sibling carries 10. Publication is signalled
 * by `plannedPublishingDate` instead — see `SupplyStateInput`.
 */
export enum AvailabilityBand {
  /** 10-19 — announced, not yet released. Sellable once published. */
  NotYetAvailable = 10,
  /** 20-29 — purchasable, including 23 (print on demand). */
  Available = 20,
  /** 30-39 — temporarily unavailable, out of stock, reprinting. */
  TemporarilyUnavailable = 30,
  /** 40-49 — withdrawn, replaced, remaindered, no longer supplied. */
  NotAvailable = 40,
}

/**
 * What a product supply offers the reader. `Unknown` means the feed data
 * carries no usable signal, so the caller keeps its own behaviour.
 */
export enum SupplyState {
  OutOfPrint = "OutOfPrint",
  OutOfStock = "OutOfStock",
  Buy = "Buy",
  Preorder = "Preorder",
  Upcoming = "Upcoming",
  Unknown = "Unknown",
}

/**
 * Parses a raw availability value into its numeric ONIX code.
 *
 * Returns `undefined` when the value is absent, empty, or not a number, so a
 * missing field never reads as code 0.
 */
export function parseAvailabilityCode(
  availability?: string | number | null,
): number | undefined {
  if (availability === null || availability === undefined) return undefined;
  const code =
    typeof availability === "number" ? availability : parseInt(availability, 10);
  return Number.isNaN(code) ? undefined : code;
}

export interface SupplyStateInput {
  /** Raw `ProductAvailability` from the feed. */
  availability?: string | number | null;
  /**
   * The announced date from `delivery/planned_pub_date`, in three states:
   *
   * - a date: the title is forthcoming. De Gruyter Brill's publication
   *   delivery drops the element, so its presence alone means "not out yet",
   *   whatever the date says and whatever the availability code says.
   * - `null`: the element is absent, so the title is published.
   * - `undefined`: the supply predates this field and has not been re-parsed.
   *   The caller then keeps its existing date-driven behaviour.
   */
  plannedPublishingDate?: Date | string | null;
  /**
   * Weeks from today until `plannedPublishingDate`: positive for a future
   * date, zero or negative once it has passed. The caller computes it with
   * its own date helper so tests stay deterministic. Pass `undefined` when the
   * date cannot be read — the supply is then withheld rather than sold.
   */
  weeksUntilPlannedPublishing?: number;
  /** How far ahead a forthcoming title opens for pre-order. Defaults to `PREORDER_WEEKS`. */
  preorderWeeks?: number;
}

/**
 * Derives the purchase state of a single product supply, the way De Gruyter
 * Brill does it: availability gates sellability, the planned publication date
 * gates release.
 *
 * Precedence, first match wins:
 *
 * 1. Availability 40-49 → `OutOfPrint`; 30-39 → `OutOfStock`. A withdrawn or
 *    out-of-stock supply is never sold, forthcoming or not.
 * 2. `plannedPublishingDate` `undefined` → `Unknown`. The supply has not been
 *    parsed with the field yet, so the caller falls back to its own path.
 * 3. `plannedPublishingDate` set → forthcoming. Within `preorderWeeks` it is a
 *    `Preorder`, further out or unreadable it is `Upcoming`. A planned date
 *    that has already passed still means forthcoming: the feed has not
 *    delivered the publication yet, so there is nothing to sell.
 * 4. `plannedPublishingDate` `null` → published. Availability 10-29 → `Buy`.
 * 5. Anything else — a published supply whose availability is absent or
 *    unrecognised — → `Unknown`, leaving the caller on its existing path.
 */
export function getSupplyState({
  availability,
  plannedPublishingDate,
  weeksUntilPlannedPublishing,
  preorderWeeks = PREORDER_WEEKS,
}: SupplyStateInput): SupplyState {
  const code = parseAvailabilityCode(availability);

  if (code !== undefined) {
    if (code >= AvailabilityBand.NotAvailable && code <= 49) {
      return SupplyState.OutOfPrint;
    }
    if (code >= AvailabilityBand.TemporarilyUnavailable && code <= 39) {
      return SupplyState.OutOfStock;
    }
  }

  if (plannedPublishingDate === undefined) return SupplyState.Unknown;

  if (plannedPublishingDate !== null) {
    if (weeksUntilPlannedPublishing === undefined) return SupplyState.Upcoming;
    return weeksUntilPlannedPublishing > preorderWeeks
      ? SupplyState.Upcoming
      : SupplyState.Preorder;
  }

  if (
    code !== undefined &&
    code >= AvailabilityBand.NotYetAvailable &&
    code <= 29
  ) {
    return SupplyState.Buy;
  }

  return SupplyState.Unknown;
}
